import Card from "components/card";
import { useEffect, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  AccordionItem,
  Accordion,
  AccordionButton,
  AccordionPanel,
} from "@chakra-ui/accordion";
import Pagination from "../../../../../components/Pagination/Pagination";
import clsx from "clsx";
import { SortBy, User } from "types/user";
import { useDebounceValue } from "usehooks-ts";
import { usersService } from "views/services/usersService";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { formatNumber } from "utils/formatNumber";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import useSWR from "swr";
import CopyButton from "components/copyButton";

const USERS_PER_PAGE = 10;

function UsersTable() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [searchDebounce] = useDebounceValue(search, 1000);
  const [openedCell, setOpenedSell] = useState("");
  const [stopAutoRefresh] = useState(false);
  const [banning, setBanning] = useState(false);
  const { data: tableData, isLoading, mutate } = useSWR(
    `/users?page=${page}&search=${searchDebounce}&limit=${USERS_PER_PAGE}`,
    () =>
      usersService.getPaginatedUsersList({
        page: page,
        pageSize: USERS_PER_PAGE,
        searchValue: searchDebounce.trim(),
        sortingValue: SortBy.CREATED_AT,
      }),
    {
      refreshInterval: 0,
      revalidateOnFocus: !stopAutoRefresh,
    }
  );

  const getUsers = () => {
    const payload = search.trim().length
      ? {
          page: page,
          pageSize: USERS_PER_PAGE,
          searchValue: search.trim(),
          sortingValue: SortBy.CREATED_AT,
        }
      : {
          page: page,
          pageSize: USERS_PER_PAGE,
          sortingValue: SortBy.CREATED_AT,
        };
    usersService.getPaginatedUsersList(payload);
  };

  useEffect(() => {
    getUsers();
  }, []);

  const handleSearch = (searchValue: string) => {
    setSearch(searchValue);
    setPage(0);
  };

  const columns = [
    columnHelper.accessor("username", {
      id: "username",
      header: () => (
        <p className="w-[170px] text-sm font-bold text-gray-600">Username</p>
      ),
      cell: (info) => (
        <p className="w-[170px] text-sm font-bold text-navy-700 text-wrap break-words">
          {info.cell.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("walletsBalances", {
      id: "walletsBalances",
      header: () => (
        <p className="w-[100px] text-sm font-bold text-gray-600">Wallet Info</p>
      ),
      cell: (info) => {
        const wallets = info.cell.getValue();
        const keys = wallets ? Object?.keys(wallets) : [];
        if (!keys?.length)
          return (
            <div className="flex flex-col w-[350px]">
              <div className="flex flex-row w-full justify-between">
                <p className="text-xs font-base text-gray-600">
                  There is no info about this user
                </p>
              </div>
            </div>
          );
        const values = Object?.values(wallets);
        const isButtonDisable = keys?.length < 2;
        const rowId = info?.cell?.id;
        const isOpen = openedCell === rowId;
        const handleToggle = () => {
          if (isButtonDisable) return;
          if (isOpen) {
            setOpenedSell("");
          } else {
            setOpenedSell(rowId);
          }
        };
        return (
          <div className="flex flex-col w-[350px]">
            <AccordionItem>
              <AccordionButton onClick={handleToggle}>
                <div className="flex flex-row w-full justify-between">
                  <p className="text-xs font-base text-gray-600 flex flex-row gap-1 items-center">
                  <CopyButton textToCopy={keys[0]} /> {keys[0]}: <b>${formatNumber(values[0] ?? 0)}</b>
                  </p>
                  {!isButtonDisable && (
                    <span>{isOpen ? <BsChevronUp /> : <BsChevronDown />}</span>
                  )}
                </div>
              </AccordionButton>

                <AccordionPanel>
                  {keys.map((key, i) => {
                    if (i === 0) {
                      return null;
                    }

                    return (
                      <p
                        className="text-xs font-base text-gray-600 mt-1 flex flex-row gap-1 items-center"
                        key={key}
                      >
                       <CopyButton textToCopy={key} /> {key}: <b>${formatNumber(values[i] ?? 0)}</b>
                      </p>
                    );
                  })}
                </AccordionPanel>

            </AccordionItem>
          </div>
        );
      },
    }),
    columnHelper.accessor('isBanned', {
      id: 'isBanned',
      header: () => (
          <p className=" w-[200px] min-w-max text-sm font-bold text-gray-600">
            Restriction
          </p>
      ),
      cell: info => (
          <div className="flex w-[200px] min-w-max items-center">
            <button disabled={banning} style={{padding: '3px 6px', border: '1px solid black', borderRadius: 8}} onClick={() => {
              handleBan(info.row.original.isBanned, info.row.original.id)
            }}>
              {info.row.original.isBanned ? 'unban' : 'ban'}
            </button>
          </div>
      ),
    }),
    // }),
    // columnHelper.accessor("transactions", {
    //   id: "transactions",
    //   header: () => (
    //     <p className="w-[100px] min-w-[100px] text-sm font-bold text-gray-600">
    //       Total number of transactions
    //     </p>
    //   ),
    //   // cell: info => (
    //   //   <p className="w-[100px] min-w-[100px] text-sm font-bold text-navy-700">
    //   //     {info.cell.getValue() || (
    //   //       <span className="text-gray-500">No Data</span>
    //   //     )}
    //   //   </p>
    //   // ),
    // }),
    // columnHelper.accessor("lastOpen", {
    //   id: "lastOpen",
    //   header: () => (
    //     <p className="w-[120px] min-w-[120px] text-sm font-bold text-gray-600">
    //       Last time user opened the app
    //     </p>
    //   ),
    // cell: info => (
    //   <p className="w-[120px] min-w-[120px] text-sm font-bold text-navy-700">
    //     {info.cell.getValue() || (
    //       <span className="text-gray-500">Unknown</span>
    //     )}
    //   </p>
    // ),
    // }),
  ];

  const [data] = useDebounceValue(tableData?.data ?? [], 100);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    debugTable: true,
  });

  const handleBan = async(isBanned: boolean, userId: number) => {
    try {
      setBanning(true);
      if (isBanned) {
        await usersService.unBanUser(userId)
      } else {
        await usersService.banUser(userId)
      }
      await mutate(prev => ({
        ...prev!,
        data: prev!.data.map(u =>
            u.id === userId ? { ...u, isBanned: !isBanned } : u
        )
      }), false);

    }  finally {
      setBanning(false)
    }
  }

  return (
    <Card extra={"w-full h-[800px] md:h-[500px] sm:overflow-auto px-6 pb-6"}>
      <header className="relative flex items-center gap-4 pt-4">
        <div className="text-xl font-bold text-navy-700">
          Users
        </div>

        <input
          type="text"
          placeholder={`Search by username`}
          className="block ml-auto min-h-7 w-full grow rounded-full bg-lightPrimary px-4 text-base font-medium text-navy-700 outline-none placeholder:!text-gray-400 sm:h-full sm:w-fit max-w-96"
          value={search}
          onChange={(e) => {
            handleSearch(e.target.value);
          }}
        />
      </header>

      <div className="mt-8 grow overflow-x-auto">
        <Accordion allowToggle>
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      className={clsx(
                        "bg-white pb-2 !w-[100px] pr-4 pt-4 text-start ",
                        header.column.getCanSort() && "cursor-pointer"
                      )}
                    >
                      <div className="flex items-center w-fit text-xs text-gray-200">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td
                    className="absolute left-1/2 -translate-x-1/2"
                    colSpan={8}
                  >
                    <p className="flex justify-center py-10">
                      <AiOutlineLoading3Quarters className="h-10 w-10 animate-spin" />
                    </p>
                  </td>
                </tr>
              )}
              {!isLoading && (data.length ?? 0) <= 0 && (
                <tr>
                  <td
                    className="absolute left-1/2 -translate-x-1/2"
                    colSpan={8}
                  >
                    <p className="py-10 text-center font-bold uppercase">
                      No data
                    </p>
                  </td>
                </tr>
              )}
              {!isLoading &&
                !!data &&
                data?.length > 0 &&
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-b last:border-b-0">
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="min-w-[100px] py-2 pr-4 first:rounded-l-lg last:rounded-r-lg last:pr-0"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </Accordion>
      </div>
      <Pagination
        currentPage={page}
        totalPages={tableData?.meta?.totalPages ?? 0}
        totalItems={tableData?.meta?.totalItems ?? 0}
        onPageChange={setPage}
      />
    </Card>
  );
}

export default UsersTable;

const columnHelper = createColumnHelper<User>();
